import { ref, computed, SetupContext } from "vue";
import axios from "axios";
import { format } from "date-fns";
import locale from "date-fns/locale/de";
import { NewsItemDetail } from "@/components/CdkNewsItem/types.d";
import createNewsItemDetail from "@/components/CdkNewsItem/createNewsItemDetail";
import getResidref from "@/utils/remoteMedia";
import { fetchThumbnail } from "@/api";
import {
  getPriorityFilterRules,
  getKeywordFillterRules,
  getGeoAreaFilterRules,
  getNewsItemStatus,
  getNewsItemStatusTooltip
} from "@/store/modules/newsItems/getters";
import { Ednote } from "@/store/modules/newsItems/types.d";
import { PlainTextProps } from "@/components/CdkNewsItem/lib/types.d";
import useConceptItems from "./useConceptItems";
import useAuthentication from "./useAuthentication";

export default function(context: SetupContext) {
  const status = ref(0);
  const newsItemDetail = ref<null | NewsItemDetail>(null);
  const imgSrc = ref("");
  const loading = ref(0);

  const { genres, ressorts } = useConceptItems();
  const { token } = useAuthentication();

  const filteredTextGenres = computed(() => {
    const isGerman = (name: any): boolean => name.locale === "de";
    const isGui = (name: any): boolean =>
      name.role === "http://g2.dpa.com/cv/dpanrol/gui";
    const matchesNameFilter = (name: any): boolean =>
      isGerman(name) && isGui(name);

    const items = genres.value
      .filter(item => item.names.some(matchesNameFilter))
      .map(item => ({
        id: item.id,
        name: item.names
          .filter(matchesNameFilter)
          .map(name => name.text)
          .join("")
      }));

    return items;
  });

  const filteredCategories = computed(() => {
    const matchesFilter = item => item.role === null && item.locale === "de";
    const items = ressorts.value
      .filter(item => item.names.some(matchesFilter))
      .map(({ id, names }) => ({
        id,
        name: names
          .filter(matchesFilter)
          .map(({ text }) => text)
          .join("")
      }));

    return items;
  });

  const by = computed(() => {
    if (newsItemDetail.value.by !== null) {
      return newsItemDetail.value.by;
    }
    return "";
  });

  const genre = computed((): string => {
    const theGenres = newsItemDetail.value.genre;

    return filteredTextGenres.value
      .filter(item => theGenres.some(theGenre => theGenre === item.id))
      .map(item => item.name)
      .join("");
  });

  const mappedCategories = computed(() => {
    const categories = newsItemDetail.value.subject.filter(
      ({ type }) => type === "category"
    );
    const mapped = filteredCategories.value.filter(item =>
      categories.some(category => category.uri === item.id)
    );
    return mapped;
  });

  const subjectFilterRules = computed(() => {
    return newsItemDetail.value.subject
      .filter(({ type }) => type === "dpasubject")
      .map(({ name }) => ({
        type: "FILTER_FULLTEXT",
        values: [name],
        text: name
      }));
  });

  const links = computed(() => {
    return newsItemDetail.value.link.map(({ href, title }) => ({
      href,
      title
    }));
  });

  const getGoogleMapsUri = (str: string): string => {
    return encodeURI(`https://www.google.com/maps/search/?api=1&query=${str}`);
  };

  const places = computed(() => {
    return newsItemDetail.value.places.map(item => ({
      lines: item.name.split(",").map(line => line.trim()),
      href: getGoogleMapsUri(item.name)
    }));
  });

  const filterByRole = (items: Ednote[], str: string): Ednote[] => {
    const PREFIX = "http://g2.dpa.com/cv/ednoterole";

    return items.filter(({ role }) => role === `${PREFIX}/${str}`);
  };

  const mapToText = (item: Ednote): string => {
    return item.text;
  };

  const edservices = computed(() => {
    return filterByRole(newsItemDetail.value.ednote, "edservice").map(
      mapToText
    );
  });

  const editorialnotes = computed(() => {
    return filterByRole(newsItemDetail.value.ednote, "editorialnote").map(
      mapToText
    );
  });

  const dpaContactsText = computed(() => {
    return filterByRole(newsItemDetail.value.ednote, "dpacontacts").map(
      mapToText
    );
  });

  const hasNotes = computed(() => {
    return (
      links.value.length > 0 ||
      places.value.length > 0 ||
      edservices.value.length > 0 ||
      editorialnotes.value.length > 0
    );
  });

  const categoryFilterRules = computed(() => {
    return mappedCategories.value.map(({ id, name }) => ({
      type: "FILTER_CATEGORY",
      values: [id],
      text: name
    }));
  });

  const tags = computed(() => {
    const { urgency, subject, keyword } = newsItemDetail.value;
    return [
      ...getPriorityFilterRules(urgency),
      // @ts-ignore
      ...categoryFilterRules.value,
      ...subjectFilterRules.value,
      ...getKeywordFillterRules(keyword),
      ...getGeoAreaFilterRules(subject)
    ];
  });

  const date = computed(() => {
    return format(newsItemDetail.value.versionCreated as Date, "DD.MM.YYYY", {
      locale
    });
  });

  const isEmbargoed = computed(() => {
    return !!newsItemDetail.value.embargoed;
  });

  const pubStatus = computed(() => {
    return getNewsItemStatus(newsItemDetail.value);
  });

  const time = computed(() => {
    return format(newsItemDetail.value.versionCreated as Date, "HH:mm", {
      locale
    });
  });

  const tooltip = computed(() => {
    return getNewsItemStatusTooltip(newsItemDetail.value);
  });

  const newsItemDetailPlainTextProps = computed(
    (): PlainTextProps => {
      if (newsItemDetail.value !== null) {
        const {
          body,
          creditLine,
          embargoed,
          guid,
          headline,
          wordCount
        } = newsItemDetail.value;

        const [title] = headline; // inconsistent APIs

        return {
          body,
          by: by.value,
          creditLine,
          date: date.value,
          dpaContactsText: dpaContactsText.value,
          editorialnotes: editorialnotes.value,
          edservices: edservices.value,
          genre: genre.value,
          hasNotes: hasNotes.value,
          isEmbargoed: isEmbargoed.value,
          links: links.value,
          places: places.value,
          pubStatus: pubStatus.value,
          tags: tags.value,
          time: time.value,
          title,
          tooltip: tooltip.value
        };
      }
      return null;
    }
  );

  const hasRemoteContent = computed(() => {
    return (
      !!newsItemDetail.value &&
      !!newsItemDetail.value.remoteContent &&
      newsItemDetail.value.remoteContent.length
    );
  });

  const residref = computed(() => {
    if (hasRemoteContent.value) {
      if (newsItemDetail.value.itemClass === "graphic") {
        return getResidref(newsItemDetail.value.remoteContent, "graphic");
      }

      return newsItemDetail.value.remoteContent[0].residref;
    }
    return null;
  });

  /**
   * Fetch news item form service, pass payload hrough view model factory
   * and update loading/http status
   */
  const fetchNewsItem = async (guid: string): Promise<any> => {
    loading.value += 1;
    newsItemDetail.value = null;

    const result = new Promise((resolve, reject) => {
      axios
        .get(`/v1/item/${guid}`, {
          baseURL: "https://cr.dpa-connect.de",
          headers: { Authorization: `Bearer ${token.value}` }
        })
        .then(({ data }) => {
          loading.value -= 1;
          status.value = 200;
          newsItemDetail.value = createNewsItemDetail(data._source); // eslint-disable-line

          if (hasRemoteContent.value) {
            loading.value += 1;

            fetchThumbnail(token.value, residref.value).then(res => {
              loading.value -= 1;
              imgSrc.value = res.data;
            });
          }

          context.emit("loaded", newsItemDetail.value);
          resolve(newsItemDetail.value);
        })
        .catch(({ response = {} }) => {
          loading.value -= 1;
          status.value = response.status;
          reject(response);
        });
    });

    return result;
  };

  return {
    fetchNewsItem,
    filteredCategories,
    filteredTextGenres,
    hasRemoteContent,
    imgSrc,
    loading,
    newsItemDetail,
    newsItemDetailPlainTextProps,
    status,
    residref
  };
}
